import { useMutation } from "@apollo/client";
import EventBusy from "@mui/icons-material/EventBusy";
import { ListItemIcon, ListItemText, MenuItem, Stack, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { useGridSelection } from "hooks/omnisearch-v2";
import pluralize from "pluralize";
import { useState } from "react";

import { SET_PORTAL_DOWNTIME_WINDOW } from "./api";
import { useBulkActionContext } from "./BulkActionsMenu";
import { DowntimeBoundPicker } from "./DowntimeBoundPicker";
import { getDowntimeWindowError } from "./downtimeWindow";
import { Row } from "./index";

export function SetDowntimeWindowMenuItem() {
  const { setDialogState } = useBulkActionContext();
  const { selectedCount } = useGridSelection();
  return (
    <MenuItem onClick={() => setDialogState("setDowntimeWindow")} disabled={selectedCount === 0}>
      <ListItemIcon>
        <EventBusy />
      </ListItemIcon>
      <ListItemText>Set Downtime Window</ListItemText>
    </MenuItem>
  );
}

export function SetDowntimeWindowDialog() {
  const alerts = useAlerts();
  const { onClose } = useBulkActionContext();
  const { selectedCount, getSelectedRows, clearSelection } = useGridSelection<Row>();
  const [downtimeWindowStart, setDowntimeWindowStart] = useState<string | null>(null);
  const [downtimeWindowEnd, setDowntimeWindowEnd] = useState<string | null>(null);

  const [setPortalDowntimeWindow, { loading }] = useMutation(SET_PORTAL_DOWNTIME_WINDOW, {
    refetchQueries: ["PortalSearch"],
  });

  const portalCount = pluralize("portal", selectedCount, true);

  const onSubmit = async () => {
    const error = getDowntimeWindowError(downtimeWindowStart, downtimeWindowEnd);
    if (error) {
      alerts.error(error);
      return;
    }
    await setPortalDowntimeWindow({
      variables: {
        ids: getSelectedRows().map((row) => row.id),
        downtimeWindowStart,
        downtimeWindowEnd,
      },
      onCompleted: () => {
        alerts.success(`Downtime window updated for ${portalCount}`);
        clearSelection();
        onClose();
      },
      onError: (mutationError) => {
        alerts.error(mutationError.message ?? "Failed to update downtime windows");
      },
    });
  };

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      title={`Set Downtime Window (${portalCount})`}
      primaryActionButtonProps={{ loading, onClick: onSubmit }}
    >
      <Stack spacing={3} sx={{ pt: 1 }}>
        <Typography variant="body2" color="text.secondary">
          While the window is active, no jobs are dispatched to the selected portals. Queued jobs
          wait and dispatch resumes automatically when the window ends. Leave both fields empty to
          clear the downtime window. Times are in your local timezone.
        </Typography>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
          <DowntimeBoundPicker
            label="Downtime starts"
            value={downtimeWindowStart}
            onChange={setDowntimeWindowStart}
          />
          <DowntimeBoundPicker
            label="Downtime ends"
            value={downtimeWindowEnd}
            onChange={setDowntimeWindowEnd}
          />
        </Stack>
      </Stack>
    </Dialog>
  );
}
